import { useEffect, useMemo, useState } from 'react';
import {
  Truck, Loader2, Plus, Trash2,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import { useAuth } from '../lib/contextoAuth';
import { useDatosDeTrabajo } from '../lib/useDatosDeTrabajo';
import { useEnLinea } from '../lib/useEnLinea';
import { leerConCopia, horaDe } from '../lib/conCopia';
import {
  registrarTraslado, recibirTraslado, pendientesTraslado, listarTraslados,
  trasladosConRecepcionPendiente, suscribirTraslados, POR_REVISAR,
} from '../lib/colaTraslados';
import AvisoCopiaLocal from '../ui/AvisoCopiaLocal';
import NotaGuardadoEnTelefono from '../integrador/pwa/NotaGuardadoEnTelefono';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import ChipEstado from '../ui/ChipEstado';
import ModalConfirmar from '../ui/ModalConfirmar';
import TiempoRelativo from '../ui/TiempoRelativo';
import { Seccion, CampoValidado } from '../ui/Formulario';
import { ESTADO_TRASLADO } from '../ui/estados';
import { Cargando } from '../ui/EstadoDePantalla';

// LO QUE VA EN CAMINO.
//
// Esta pantalla existe para contestar una pregunta que antes no contestaba
// nadie: «¿dónde está lo que mandé el martes?». Antes de la migración 068
// la respuesta del sistema era «ya llegó» — y era falsa mientras la
// mercancía iba en la camioneta.
//
// POR ESO LO QUE VA EN CAMINO VA ARRIBA Y EN AMARILLO. Un traslado
// recibido es un dato; uno en camino es alguien esperando en un andén.

const num = (v) => Number(v || 0).toLocaleString('es-MX');

// DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §4.6; parche del integrador para
// experiencia). Un traslado guardado sin señal se pinta en la lista con un
// estado propio, sin tocar `ui/estados.js`: es local, todavía no tiene
// folio y lo único que se puede hacer con él es ver qué lleva.
const ESTADO_LOCAL = {
  ...ESTADO_TRASLADO,
  PENDIENTE_ENVIO: { etiqueta: 'Pendiente de enviar', color: 'gris' },
};
const filaLocal = (e) => ({
  id: `local:${e.clave_idempotencia}`, _local: true, _hora: new Date(e._ts), _cuerpo: e.cuerpo,
  folio: null, estado: 'PENDIENTE_ENVIO',
  origen: e.resumen?.origen, destino: e.resumen?.destino,
  renglones: e.resumen?.renglones ?? 0, unidades: e.resumen?.unidades ?? 0,
  referencia: e.resumen?.referencia ?? null, enviado_en: null,
});

export default function Traslados({ irA }) {
  const { usuario } = useAuth();
  const idUsuario = usuario?.id;
  const enLinea = useEnLinea();
  // La lista pasa por `useDatosDeTrabajo`: sin señal se enseña la copia CON
  // su hora. `datos === null` es «cargando»; con error, la lista dice el
  // error y «Mandar mercancía» sigue donde siempre (P12, 29 sep 2026).
  const { datos: filas, error, momento, recargar: cargar } = useDatosDeTrabajo('/traslados');
  const [enviando, setEnviando] = useState(false);
  const [recibiendo, setRecibiendo] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  const [viendo, setViendo] = useState(null);
  const avisos = useAvisos();

  // Lo guardado en este teléfono: traslados por enviar, recepciones por
  // enviar (marcan su fila del servidor) y lo que el servidor rechazó.
  const [local, setLocal] = useState({ filas: [], recepciones: new Set(), porRevisar: 0 });
  useEffect(() => {
    if (idUsuario === undefined || idUsuario === null) return undefined;
    let vivo = true;
    const leer = () => Promise.all([
      pendientesTraslado(idUsuario), trasladosConRecepcionPendiente(idUsuario),
      listarTraslados(idUsuario, { estado: POR_REVISAR }),
    ]).then(([pend, rec, rev]) => {
      if (!vivo) return;
      setLocal({
        filas: pend.filter((e) => e.accion !== 'RECIBIR').map(filaLocal),
        recepciones: rec, porRevisar: rev.length,
      });
    }).catch(() => {});
    leer();
    const dejar = suscribirTraslados(leer);
    return () => { vivo = false; dejar(); };
  }, [idUsuario]);

  const enCamino = (filas || []).filter((t) => t.estado === 'EN_CAMINO');
  const conFaltantes = (filas || []).filter(
    (t) => t.estado === 'RECIBIDO_CON_FALTANTES');

  const columnas = [
    { etiqueta: 'Folio', valor: (t) => (t._local ? '—' : String(t.folio)) },
    {
      etiqueta: 'Estado',
      valor: (t) => t.estado,
      render: (t) => (
        <span className="inline-flex flex-wrap gap-1">
          <ChipEstado mapa={ESTADO_LOCAL} clave={t.estado} />
          {local.recepciones.has(t.id) && (
            <ChipEstado mapa={ESTADO_LOCAL} clave="PENDIENTE_ENVIO" />
          )}
        </span>
      ),
    },
    {
      etiqueta: 'De dónde a dónde',
      valor: (t) => `${t.origen} → ${t.destino}`,
      render: (t) => (
        <span>
          <span className="font-mono">{t.origen}</span>
          <span className="mx-1 text-gray-400">→</span>
          <span className="font-mono">{t.destino}</span>
          {t.sucursal_origen && t.sucursal_destino && (
            <span className="block text-xs text-gray-500">
              {t.sucursal_origen} → {t.sucursal_destino}
            </span>
          )}
        </span>
      ),
    },
    {
      etiqueta: 'Qué lleva',
      alineado: 'derecha',
      valor: (t) => `${t.renglones} producto(s) · ${num(t.unidades)} u.`,
    },
    { etiqueta: 'Guía o placas', valor: (t) => t.referencia || '—' },
    {
      etiqueta: 'Salió',
      valor: (t) => (t._local ? `Guardado en este teléfono a las ${horaDe(t._hora)}` : t.enviado_en),
      render: (t) => (t._local ? (
        <span className="text-gray-600">Guardado en este teléfono a las {horaDe(t._hora)}</span>
      ) : (
        <span className="text-gray-600">
          <TiempoRelativo valor={t.enviado_en} />
          {t.envio && <span className="block text-xs text-gray-600">{t.envio}</span>}
        </span>
      )),
    },
  ];

  const acciones = (t) => {
    const lista = [{ etiqueta: 'Ver qué lleva', onClick: () => setViendo(t) }];
    if (t._local) return lista;
    if (t.estado === 'EN_CAMINO') {
      // Con una recepción ya guardada para este traslado no se ofrece
      // recibirlo otra vez; se enviará sola al volver la señal.
      if (!local.recepciones.has(t.id)) lista.unshift({ etiqueta: 'Recibir', onClick: () => setRecibiendo(t) });
      // Cancelar lo que el servidor quizá ya recibió es un conflicto que se
      // resuelve con señal: sin ella la acción no se ofrece y la nota de
      // arriba dice por qué (DISENO-F2 §4.6).
      if (enLinea) {
        lista.push({
          etiqueta: 'Se dio la vuelta', peligro: true,
          onClick: () => setCancelando(t),
        });
      }
    }
    return lista;
  };
  const filasVisibles = [...local.filas, ...(filas || [])];

  // P12 (29 sep 2026): antes, si la lista no cargaba, la pantalla ENTERA se
  // cambiaba por el mensaje y «Mandar mercancía» desaparecía; el censo de
  // la 3.15.164 lo midió así en el perfil de la operadora («sin reacción»).
  // Mandar no depende de la lista: el error va dentro de la lista, con su
  // «Reintentar», y el botón sigue donde siempre.
  return (
    <div className="space-y-4">
      {/* LO QUE VA EN CAMINO ES LO ÚNICO QUE RECLAMA A ALGUIEN. */}
      <AvisoCopiaLocal momento={momento} que="Los traslados" />

      {local.porRevisar > 0 && (
        <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="min-w-0 flex-1 basis-56">
            {local.porRevisar === 1 ? '1 traslado guardado sin señal no se pudo enviar' : `${local.porRevisar} traslados guardados sin señal no se pudieron enviar`}: el servidor lo rechazó.
          </span>
          <button type="button" onClick={() => irA?.('pendientes-de-enviar')}
                  className="min-h-11 rounded-lg px-3 font-semibold underline focus:outline-none focus:ring-2 focus:ring-amber-700">
            Ver en Pendientes de enviar →
          </button>
        </div>
      )}

      {enCamino.length > 0 && (
        <NotaExplicativa titulo={`${enCamino.length} traslado(s) en camino`}>
          Esta mercancía ya salió y no ha llegado: está contada en
          tránsito, no en su destino.{' '}
          <strong>Cuando llegue el camión, pulsa «Recibir»</strong> y anota
          lo que de verdad bajó.
          {!enLinea && (
            <>
              {' '}Sin señal no se puede cancelar («Se dio la vuelta»): hay que
              comprobar con el servidor que nadie lo recibió ya.
            </>
          )}
        </NotaExplicativa>
      )}

      {/* Un faltante sin resolver es mercancía que nadie está buscando. */}
      {conFaltantes.length > 0 && (
        <NotaExplicativa
          titulo={`${conFaltantes.length} traslado(s) llegaron incompletos`}>
          De estos llegó menos de lo que salió. Lo que falta{' '}
          <strong>sigue contado en la ubicación TRANSITO</strong>: no se ha
          perdido ni se ha dado por llegado. Búscalo en el camión y, si de
          verdad no aparece, dalo de baja como merma desde ahí.
        </NotaExplicativa>
      )}

      <ListaEstandar
        titulo="Traslados"
        subtitulo="Mercancía que viaja entre sitios. Sale ahora y se confirma al recibirla; mientras tanto queda contada en camino."
        accionPrincipal={{
          etiqueta: 'Mandar mercancía',
          onClick: () => setEnviando(true),
          icono: Plus,
        }}
        columnas={columnas}
        filas={filasVisibles}
        cargando={filas === null && !error}
        acciones={acciones}
        error={error || null}
        onReintentar={cargar}
        claveFila={(t) => t.id}
        exportar={{ nombre: 'traslados' }}
        vacio={(
          <EstadoVacio
            icono={Truck}
            titulo="Todavía no has mandado nada entre sitios"
            descripcion="Un traslado sirve para mover mercancía a otra sucursal sin que el sistema la dé por llegada antes de tiempo: sale del origen, queda contada en camino, y llega cuando alguien confirma que llegó."
            accion={(
              <button type="button" onClick={() => setEnviando(true)}
                      className="inline-flex items-center gap-1 rounded-xl
                                 bg-[var(--color-marca)] px-4 py-2
                                 font-semibold text-white">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Mandar mercancía
              </button>
            )}
          />
        )}
      />

      {enviando && (
        <ModalEnviar
          idUsuario={idUsuario}
          onCerrar={() => setEnviando(false)}
          onListo={(m) => { setEnviando(false); avisos.exito(m); cargar(); }}
        />
      )}
      {recibiendo && (
        <ModalRecibir
          t={recibiendo}
          idUsuario={idUsuario}
          enLinea={enLinea}
          onCerrar={() => setRecibiendo(null)}
          onListo={(m) => { setRecibiendo(null); avisos.exito(m); cargar(); }}
        />
      )}
      {cancelando && (
        <ModalConfirmar
          titulo={`¿La camioneta del traslado ${cancelando.folio} se dio la vuelta?`}
          descripcion="Todo lo que iba dentro vuelve a su ubicación de origen, con su movimiento de vuelta. No se borra nada: el viaje de ida y el de vuelta quedan los dos apuntados."
          etiquetaConfirmar="Sí, volvió todo al origen"
          peligro
          onConfirmar={async () => {
            const r = await api.post(`/traslados/${cancelando.id}/cancelar`);
            setCancelando(null);
            avisos.exito(r.mensaje);
            cargar();
          }}
          onCerrar={() => setCancelando(null)}
        />
      )}
      {viendo && (
        <DetalleTraslado t={viendo} onCerrar={() => setViendo(null)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------ enviar
function ModalEnviar({ idUsuario, onCerrar, onListo }) {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [productos, setProductos] = useState([]);
  // La hora de la copia si los catálogos salieron del teléfono (null = API).
  const [copiaDe, setCopiaDe] = useState(null);
  const [f, setF] = useState({
    ubicacion_origen: '', ubicacion_destino: '', referencia: '', motivo: '',
  });
  const [renglones, setRenglones] = useState([{ sku: '', cantidad: '' }]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // Los dos catálogos con copia (DISENO-F2 §4.6): sin señal el modal no
  // llega vacío, y lo dice con la hora de la copia.
  useEffect(() => {
    leerConCopia(idUsuario, '/ubicaciones')
      .then(({ datos, momento }) => { setUbicaciones(datos); if (momento) setCopiaDe(momento); })
      .catch(() => setUbicaciones([]));
    leerConCopia(idUsuario, '/productos?limite=500')
      .then(({ datos, momento }) => { setProductos(datos); if (momento) setCopiaDe(momento); })
      .catch(() => setProductos([]));
  }, [idUsuario]);

  // NO SE OFRECE COMO DESTINO LO QUE NO RECIBE ENTRADAS. Ofrecerlo y
  // rechazarlo al guardar es hacerle perder el rato a alguien; la
  // Doctrina 2 dice prevenir, no regañar.
  const posiblesDestino = useMemo(
    () => ubicaciones.filter(
      (u) => u.recibe_entradas !== false && !u.bloqueada
             && u.codigo !== f.ubicacion_origen),
    [ubicaciones, f.ubicacion_origen],
  );
  const posiblesOrigen = useMemo(
    () => ubicaciones.filter((u) => u.guarda_mercancia !== false && !u.bloqueada),
    [ubicaciones],
  );

  const origen = ubicaciones.find((u) => u.codigo === f.ubicacion_origen);
  const destino = ubicaciones.find((u) => u.codigo === f.ubicacion_destino);
  const viaja = origen && destino
    && origen.id_sucursal && destino.id_sucursal
    && origen.id_sucursal !== destino.id_sucursal;

  const llenos = renglones.filter((r) => r.sku.trim() && Number(r.cantidad) > 0);
  const falta = !f.ubicacion_origen ? 'Elige de dónde sale.'
    : !f.ubicacion_destino ? 'Elige a dónde va.'
      : llenos.length === 0 ? 'Añade al menos un producto con su cantidad.'
        : null;

  return (
    <ModalConfirmar
      titulo="Mandar mercancía a otro sitio"
      descripcion="Sale ahora del origen y queda contada en camino. Llega cuando alguien la reciba en el destino."
      etiquetaConfirmar="Mandar y ponerla en camino"
      extra={(
        <div className="space-y-5">
          <Seccion titulo="El viaje">
            <CampoValidado etiqueta="Sale de" requerido valor={f.ubicacion_origen}>
              {(props) => (
                <select {...props} value={f.ubicacion_origen}
                        onChange={set('ubicacion_origen')}>
                  <option value="">Elige…</option>
                  {posiblesOrigen.map((u) => (
                    <option key={u.id} value={u.codigo}>
                      {u.codigo} · {u.nombre}
                      {u.sucursal ? ` (${u.sucursal})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </CampoValidado>
            <CampoValidado etiqueta="Va a" requerido valor={f.ubicacion_destino}
                           ayuda="Solo aparecen los sitios que reciben entradas.">
              {(props) => (
                <select {...props} value={f.ubicacion_destino}
                        onChange={set('ubicacion_destino')}>
                  <option value="">Elige…</option>
                  {posiblesDestino.map((u) => (
                    <option key={u.id} value={u.codigo}>
                      {u.codigo} · {u.nombre}
                      {u.sucursal ? ` (${u.sucursal})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </CampoValidado>
            <CampoValidado
              etiqueta="Guía, placas o número de sello"
              ayuda="Opcional, y es lo que se teclea para buscar un envío cuando alguien llama preguntando."
              valor={f.referencia}>
              {(props) => <input {...props} value={f.referencia}
                                 onChange={set('referencia')}
                                 placeholder="ABC-1234" />}
            </CampoValidado>
            <CampoValidado etiqueta="Motivo" ayuda="Opcional." valor={f.motivo}>
              {(props) => <input {...props} value={f.motivo}
                                 onChange={set('motivo')}
                                 placeholder="Reposición semanal" />}
            </CampoValidado>
          </Seccion>

          {/* Se dice ANTES de guardar por qué esto va a tardar dos pasos. */}
          {viaja && (
            <p className="rounded-lg bg-amber-50 border border-amber-200 p-2
                          text-sm text-amber-900">
              Van a sucursales distintas, así que la mercancía viaja: saldrá
              de <strong>{origen.sucursal}</strong> y no contará en{' '}
              <strong>{destino.sucursal}</strong> hasta que alguien la
              reciba.
            </p>
          )}

          <Seccion titulo="Qué va dentro" columnas={1}>
            <div className="space-y-2">
              <NotaGuardadoEnTelefono momento={copiaDe} que="la lista de sitios y productos" />
              {renglones.map((r, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <input
                    className="entrada flex-1" list="productos-traslado"
                    value={r.sku} placeholder="SKU"
                    onChange={(e) => setRenglones((s) => s.map(
                      (x, j) => (j === i ? { ...x, sku: e.target.value } : x)))}
                  />
                  <input
                    className="entrada w-28" type="number" min="0" step="any"
                    value={r.cantidad} placeholder="Cantidad"
                    onChange={(e) => setRenglones((s) => s.map(
                      (x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)))}
                  />
                  <button
                    type="button" aria-label={`Quitar el renglón ${i + 1}`}
                    disabled={renglones.length === 1}
                    title={renglones.length === 1 ? 'Un traslado lleva al menos un renglón: este no se puede quitar, pero sí cambiar.' : 'Quitar este renglón'}
                    onClick={() => setRenglones((s) => s.filter((_, j) => j !== i))}
                    className="p-2 text-gray-500 disabled:text-gray-300">
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <datalist id="productos-traslado">
                {productos.map((p) => (
                  <option key={p.sku} value={p.sku}>{p.nombre}</option>
                ))}
              </datalist>
              <button
                type="button"
                onClick={() => setRenglones((s) => [...s, { sku: '', cantidad: '' }])}
                className="inline-flex items-center gap-1 text-sm font-semibold
                           text-[var(--color-marca)]">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Añadir otro producto
              </button>
            </div>
          </Seccion>

          {/* Botón apagado CON LA RAZÓN A LA VISTA. */}
          {falta && <p className="text-sm text-gray-600">{falta}</p>}
        </div>
      )}
      onConfirmar={async () => {
        if (falta) throw new ErrorApi(falta);
        // Por la cola (DISENO-F2 §4.6): con señal sale ahora, con su clave;
        // sin señal queda guardado en el teléfono y se envía solo.
        const r = await registrarTraslado(idUsuario, {
          ubicacion_origen: f.ubicacion_origen,
          ubicacion_destino: f.ubicacion_destino,
          referencia: f.referencia.trim() || null,
          motivo: f.motivo.trim() || null,
          renglones: llenos.map((x) => ({
            sku: x.sku.trim().toUpperCase(), cantidad: String(x.cantidad),
          })),
        });
        onListo(r.estado === 'aplicado'
          ? r.respuesta.mensaje
          : 'Traslado guardado en este teléfono. Se enviará solo al volver la señal y entonces recibirá su folio.');
      }}
      onCerrar={onCerrar}
    />
  );
}

// ----------------------------------------------------------- recibir
//
// EL VALOR POR OMISIÓN ES «LLEGÓ TODO», y es la decisión que hace esta
// pantalla usable. Obligar a teclear cada línea que sí llegó es como
// nadie recibe nada: lo excepcional es el faltante, y eso es lo único
// que hay que escribir.
function ModalRecibir({ t, idUsuario, enLinea = true, onCerrar, onListo }) {
  const [detalle, setDetalle] = useState(null);
  const [copiaDe, setCopiaDe] = useState(null);
  // Sin señal y sin copia del detalle: no hay renglones que anotar, pero sí
  // se puede confirmar que llegó todo (DISENO-F2 §4.6).
  const [sinDetalle, setSinDetalle] = useState(false);
  const [cantidades, setCantidades] = useState({});
  const [nota, setNota] = useState('');

  useEffect(() => {
    leerConCopia(idUsuario, `/traslados/${t.id}`)
      .then(({ datos, momento }) => { setDetalle(datos); setCopiaDe(momento); })
      .catch(() => { setDetalle(null); setSinDetalle(true); });
  }, [t.id, idUsuario]);

  const soloLlegoTodo = sinDetalle && !enLinea;
  const renglones = detalle?.renglones || [];
  const faltantes = renglones.filter((r) => {
    const v = cantidades[r.sku];
    return v !== undefined && v !== '' && Number(v) < Number(r.cantidad_enviada);
  });

  return (
    <ModalConfirmar
      titulo={`Recibir el traslado ${t.folio}`}
      descripcion="Anota solo lo que llegó DE MENOS. Lo que no toques se da por llegado completo."
      etiquetaConfirmar={soloLlegoTodo ? 'Confirmar: llegó todo' : 'Confirmar lo que llegó'}
      extra={(
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Viene de <span className="font-mono">{t.origen}</span> y llega a{' '}
            <span className="font-mono">{t.destino}</span>
            {t.referencia && <> · guía <strong>{t.referencia}</strong></>}
          </p>

          <NotaGuardadoEnTelefono momento={copiaDe} que="la lista de lo que venía" />

          {soloLlegoTodo && (
            <p role="status" className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              Sin señal y este traslado no está guardado en el teléfono. Puedes
              confirmar que <strong>llegó todo</strong>; si faltó algo, espera a
              tener señal para anotarlo renglón por renglón.
            </p>
          )}

          {detalle === null && !soloLlegoTodo && !sinDetalle && (
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Cargando lo que venía…
            </p>
          )}
          {detalle === null && sinDetalle && enLinea && (
            <p role="alert" className="text-sm text-red-700">
              No se pudo traer lo que venía en este traslado. Revisa la señal y vuelve a abrirlo; si sigue igual, confirma con la lista en papel.
            </p>
          )}
          {detalle !== null && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Producto</th>
                    <th className="py-1 text-right">Salió</th>
                    <th className="py-1 text-right">Llegó</th>
                  </tr>
                </thead>
                <tbody>
                  {renglones.map((r) => (
                    <tr key={r.sku} className="border-t border-gray-100">
                      <td className="py-1.5">
                        <span className="font-mono">{r.sku}</span>
                        <span className="block text-xs text-gray-500">
                          {r.nombre}
                          {r.codigo_lote && ` · lote ${r.codigo_lote}`}
                        </span>
                      </td>
                      <td className="py-1.5 text-right">
                        {num(r.cantidad_enviada)}
                      </td>
                      <td className="py-1.5 text-right">
                        <input
                          type="number" min="0" step="any"
                          className="entrada w-24 text-right"
                          placeholder={num(r.cantidad_enviada)}
                          aria-label={`Cuánto llegó de ${r.sku}`}
                          value={cantidades[r.sku] ?? ''}
                          onChange={(e) => setCantidades((s) => ({
                            ...s, [r.sku]: e.target.value,
                          }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {faltantes.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3
                            text-sm text-amber-900">
              <p className="font-semibold">
                Van a faltar {faltantes.length} producto(s).
              </p>
              <p>
                Lo que falte <strong>se queda contado en TRANSITO</strong>: no
                se da por perdido ni por llegado. Si después aparece, entra
                con un traspaso desde ahí; si no aparece, se da de baja como
                merma.
              </p>
            </div>
          )}

          <label className="block text-sm">
            <span className="font-semibold text-gray-700">
              Nota de la recepción
            </span>
            <input className="entrada mt-1 w-full" value={nota}
                   onChange={(e) => setNota(e.target.value)}
                   placeholder="Faltaban tres cajas, el sello venía roto…" />
          </label>
        </div>
      )}
      onConfirmar={async () => {
        const dichos = soloLlegoTodo ? [] : Object.entries(cantidades)
          .filter(([, v]) => v !== '' && v !== undefined)
          .map(([sku, v]) => ({ sku, cantidad: String(v) }));
        // Por la cola, con clave (R-PWA-12): el reenvío devuelve lo ya aplicado.
        const r = await recibirTraslado(idUsuario, t.id, {
          renglones: dichos, nota: nota.trim() || null,
        }, { folio: t.folio, origen: t.origen, destino: t.destino });
        onListo(r.estado === 'aplicado'
          ? r.respuesta.mensaje
          : 'Recepción guardada en este teléfono. Se enviará sola al volver la señal.');
      }}
      onCerrar={onCerrar}
    />
  );
}

// ----------------------------------------------------------- detalle
function DetalleTraslado({ t, onCerrar }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    // Un traslado guardado en el teléfono enseña lo que lleva desde su
    // propio cuerpo: todavía no existe en el servidor.
    if (t._local) {
      Promise.resolve().then(() => setD({
        renglones: (t._cuerpo?.renglones || []).map((r) => ({
          sku: r.sku, cantidad_enviada: r.cantidad, cantidad_recibida: null, falta: 0,
        })),
      }));
      return;
    }
    api.get(`/traslados/${t.id}`).then(setD).catch(() => setD(null));
  }, [t.id, t._local, t._cuerpo]);

  return (
    <ModalConfirmar
      titulo={t._local ? 'Traslado guardado en este teléfono' : `Traslado ${t.folio}`}
      descripcion={`De ${t.origen} a ${t.destino}.`}
      etiquetaConfirmar="Cerrar"
      onConfirmar={async () => onCerrar()}
      onCerrar={onCerrar}
      extra={(
        <div className="space-y-3 text-sm">
          <p className="flex flex-wrap items-center gap-2">
            <ChipEstado mapa={ESTADO_LOCAL} clave={t.estado} />
            {t._local && <span className="text-gray-600">Guardado a las {horaDe(t._hora)} · se enviará solo al volver la señal</span>}
            {t.envio && <span className="text-gray-600">Mandó {t.envio}</span>}
            {t.recibio && <span className="text-gray-600">· Recibió {t.recibio}</span>}
          </p>
          {t.nota_recepcion && (
            <p className="rounded bg-gray-50 p-2 text-gray-700">
              {t.nota_recepcion}
            </p>
          )}
          {d === null ? (
            <Cargando filas={4} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Producto</th>
                    <th className="py-1 text-right">Salió</th>
                    <th className="py-1 text-right">Llegó</th>
                    <th className="py-1 text-right">Falta</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.renglones || []).map((r) => (
                    <tr key={r.sku} className="border-t border-gray-100">
                      <td className="py-1.5 font-mono">{r.sku}</td>
                      <td className="py-1.5 text-right">{num(r.cantidad_enviada)}</td>
                      <td className="py-1.5 text-right">
                        {r.cantidad_recibida === null
                          ? <span className="text-gray-400">en camino</span>
                          : num(r.cantidad_recibida)}
                      </td>
                      <td className={`py-1.5 text-right ${
                        Number(r.falta) > 0 ? 'font-semibold text-amber-700' : ''}`}>
                        {Number(r.falta) > 0 ? num(r.falta) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    />
  );
}
